import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

function ArrowRightIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

function Slide({ slide, index, current, handleSlideClick, size }) {
  const slideRef = useRef(null);
  const xRef = useRef(0);
  const yRef = useRef(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const animate = () => {
      if (!slideRef.current) return;
      slideRef.current.style.setProperty("--x", `${xRef.current}px`);
      slideRef.current.style.setProperty("--y", `${yRef.current}px`);
      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => frameRef.current && cancelAnimationFrame(frameRef.current);
  }, []);

  const handleMouseMove = (event) => {
    const el = slideRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    xRef.current = event.clientX - (r.left + Math.floor(r.width / 2));
    yRef.current = event.clientY - (r.top + Math.floor(r.height / 2));
  };

  const handleMouseLeave = () => {
    xRef.current = 0;
    yRef.current = 0;
  };

  const active = current === index;

  const sizeCls =
    size === "sm"
      ? [
          "w-[58vmin] h-[72vmin]",
          "min-w-[220px] min-h-[280px]",
          "max-w-[320px] max-h-[400px]",
          "md:w-[40vmin] md:h-[48vmin]",
          "md:min-w-[300px] md:min-h-[380px]",
          "md:max-w-[420px] md:max-h-[520px]",
        ].join(" ")
      : [
          "w-[72vw] h-[92vw]",
          "min-w-[260px] min-h-[330px]",
          "max-w-[370px] max-h-[470px]",
          "sm:w-[64vmin] sm:h-[78vmin]",
          "md:w-[42vmin] md:h-[52vmin]",
          "md:min-w-[340px] md:min-h-[430px]",
          "md:max-w-[500px] md:max-h-[620px]",
        ].join(" ");

  return (
    <div className="[perspective:1400px] [transform-style:preserve-3d] shrink-0">
      <li
        ref={slideRef}
        onClick={() => handleSlideClick(index)}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className={[
          "relative z-10",
          "flex flex-col items-center justify-end",
          sizeCls,
          "rounded-[28px] overflow-hidden",
          "cursor-pointer select-none",
          "transition-[transform,opacity,filter] duration-500",
          "shadow-[0_24px_60px_rgba(0,0,0,0.18)]",
        ].join(" ")}
        style={{
          transform: active
            ? "scale(1) rotateX(0deg) translateY(0)"
            : "scale(0.92) rotateX(8deg) translateY(10px)",
          transformOrigin: "bottom center",
          opacity: active ? 1 : 0.68,
          filter: active ? "saturate(1)" : "saturate(.85)",
        }}
      >
        <div
          className="absolute inset-0 transition-transform duration-200 ease-out"
          style={{
            transform: active
              ? "translate3d(calc(var(--x) / 34), calc(var(--y) / 34), 0)"
              : "none",
          }}
        >
          <img
            src={slide.src}
            alt={slide.title}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ opacity: active ? 1 : 0.72 }}
            loading="eager"
            decoding="sync"
          />

          <div
            className="absolute inset-0 transition-all duration-700"
            style={{
              background: active
                ? "linear-gradient(to top, rgba(54,107,99,0.82) 0%, rgba(54,107,99,0.32) 44%, rgba(0,0,0,0.04) 100%)"
                : "linear-gradient(to top, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.18) 50%, rgba(0,0,0,0.02) 100%)",
            }}
          />
        </div>

        <article
          className={[
            "relative z-10 w-full px-5 pb-6 pt-16 text-center md:px-7 md:pb-7",
            "transition-opacity duration-500",
            active ? "opacity-100" : "opacity-0",
          ].join(" ")}
        >
          <h2 className="text-xl font-semibold text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.45)] md:text-3xl">
            {slide.title}
          </h2>
        </article>
      </li>
    </div>
  );
}

export function CarouselControl({ type, title, handleClick, className = "" }) {
  return (
    <button
      className={[
        "flex h-11 w-11 items-center justify-center rounded-full",
        "border border-[color:var(--border)] bg-white/85 backdrop-blur-md",
        "shadow-[0_12px_30px_rgba(0,0,0,0.08)]",
        "transition hover:bg-white active:scale-[0.98]",
        type === "previous" ? "rotate-180" : "",
        className,
      ].join(" ")}
      title={title}
      onClick={handleClick}
      type="button"
    >
      <ArrowRightIcon className="h-5 w-5 text-[color:var(--ink)]" />
    </button>
  );
}

export default function Carousel3D({
  slides = [],
  activeIndex,
  onActiveChange,
  size = "md",
  showControls = true,
}) {
  const isControlled = typeof activeIndex === "number";
  const [internalCurrent, setInternalCurrent] = useState(0);
  const current = isControlled ? activeIndex : internalCurrent;

  const ulRef = useRef(null);
  const [trackX, setTrackX] = useState(null);

  const setCurrent = (next) => {
    if (!slides?.length) return;
    const safe = ((next % slides.length) + slides.length) % slides.length;
    if (!isControlled) setInternalCurrent(safe);
    onActiveChange?.(safe);
  };

  useLayoutEffect(() => {
    const ul = ulRef.current;
    if (!ul) return;
    const container = ul.parentElement;
    if (!container) return;

    const recalc = () => {
      const item = ul.children[current];
      if (!item) return;
      // offsetLeft/offsetWidth ignore CSS transforms, so they give us the
      // natural layout position regardless of any scale on the card or
      // translate on the track itself.
      const itemLeft = item.offsetLeft;
      const itemWidth = item.offsetWidth;
      const containerWidth = container.offsetWidth;
      setTrackX(containerWidth / 2 - itemLeft - itemWidth / 2);
    };

    recalc();
    window.addEventListener("resize", recalc);
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(recalc) : null;
    if (ro) ro.observe(container);
    return () => {
      window.removeEventListener("resize", recalc);
      if (ro) ro.disconnect();
    };
  }, [current, slides.length, size]);

  useEffect(() => {
    if (!slides.length) return;
    if (current > slides.length - 1) {
      setCurrent(0);
    }
  }, [slides.length]);

  const handlePreviousClick = () => setCurrent(current - 1);
  const handleNextClick = () => setCurrent(current + 1);
  const handleSlideClick = (index) => setCurrent(index);

  const id = useId();

  if (!slides?.length) return null;

  return (
    <div
      className="relative w-full overflow-hidden"
      aria-labelledby={`carousel-heading-${id}`}
    >
      <div className="mx-auto w-full max-w-6xl px-2 py-4 md:px-4 md:py-6">
        <div className="flex flex-col">
          <div className="relative min-h-[360px] md:min-h-[360px]">
            <ul
              ref={ulRef}
              className={`flex items-center gap-8 ${
                trackX !== null
                  ? "transition-transform duration-700 ease-[cubic-bezier(.22,1,.36,1)]"
                  : ""
              }`}
              style={{
                transform: trackX !== null ? `translateX(${trackX}px)` : "none",
                visibility: trackX !== null ? "visible" : "hidden",
              }}
            >
              {slides.map((slide, index) => (
                <Slide
                  key={`${slide.title}-${index}`}
                  slide={slide}
                  index={index}
                  current={current}
                  handleSlideClick={handleSlideClick}
                  size={size}
                />
              ))}
            </ul>
          </div>

          {showControls && slides.length > 1 && (
            <div className="mt-5 flex h-[64px] items-center justify-center gap-4">
              <CarouselControl
                type="previous"
                title="Anterior"
                handleClick={handlePreviousClick}
              />
              <CarouselControl
                type="next"
                title="Siguiente"
                handleClick={handleNextClick}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}